// src/pages/auth/LoginPage.tsx
import React, { useState, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, Lock, ArrowLeft, KeyRound, Mail } from 'lucide-react';

interface LoginPageProps {
  onShowSignup?: () => void;
  onLoginSuccess?: () => void;
  initialUserType?: 'buyer' | 'seller';
}

const LoginPage: React.FC<LoginPageProps> = ({ onShowSignup, onLoginSuccess, initialUserType = 'buyer' }) => {
  const { user, login } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  // --- States ---
  const [view, setView] = useState<'login' | 'forgot' | 'reset'>('login'); // To toggle views
  const [userType, setUserType] = useState<'buyer' | 'seller'>(initialUserType);
  
  // Login State
  const [formData, setFormData] = useState({ email: '', password: '' });
  
  // Forgot Password State
  const [resetIdentifier, setResetIdentifier] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Sync props
  useEffect(() => { setUserType(initialUserType); }, [initialUserType]);

  // Login Handler
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const endpoint = userType === 'buyer' ? 'buyers' : 'sellers';
      const response = await fetch(`/springboot/api/${endpoint}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, password: formData.password, userType }),
      });
      const data = await response.json();

      if (response.ok) {
        login(data);
        if (onLoginSuccess) onLoginSuccess();
        navigate(userType === 'buyer' ? '/buyer/profile' : '/seller/dashboard');
      } else {
        setMessage({ type: 'error', text: data.message || 'Login failed.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Server error. Try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Send OTP for Forgot Password
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch('/springboot/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: resetIdentifier, userType }),
      });
      const data = await response.json();

      if (response.ok) {
        setMaskedEmail(data.maskedEmail); // Backend se masked email mila
        setMessage({ type: 'success', text: `OTP sent to ${data.maskedEmail}` });
        setView('reset'); // Move to Step 2
      } else {
        setMessage({ type: 'error', text: typeof data === 'string' ? data : 'User not found.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to send OTP.' });
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP & Set New Password
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch('/springboot/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          identifier: resetIdentifier, 
          otp, 
          newPassword, 
          userType 
        }),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Password reset successfully! Please login.' });
        setTimeout(() => setView('login'), 2000); // Back to login after 2 sec
      } else {
        const text = await response.text();
        setMessage({ type: 'error', text: text });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Reset failed. Try again.' });
    } finally {
      setLoading(false);
    }
  };

  // --- RENDER ---
  return (
    <div className="flex flex-col items-center justify-center h-full w-full">
      <div className="w-full max-w-sm">
        
        {/* HEADER */}
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-2">
          {view === 'login' ? `Login as ${userType === 'buyer' ? 'Buyer' : 'Seller'}` : 'Reset Password'}
        </h2>
        
        {view === 'login' && (
           <p className="text-center text-gray-500 mb-6 text-sm">Enter your email or mobile number.</p>
        )}
        
        {/* MESSAGE BOX */}
        {message && (
          <div className={`p-3 mb-4 rounded-md text-center text-sm ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {message.text}
          </div>
        )}

        {/* --- VIEW 1: LOGIN FORM --- */}
        {view === 'login' && (
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400"><User size={18} /></span>
              <input
                type="text" // Changed from email to text
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full pl-10 pr-3 py-2 border-b border-gray-300 focus:outline-none focus:border-purple-500 transition-colors"
                placeholder="Email or Mobile Number"
                required
              />
            </div>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400"><Lock size={18} /></span>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="w-full pl-10 pr-3 py-2 border-b border-gray-300 focus:outline-none focus:border-purple-500 transition-colors"
                placeholder="Password"
                required
              />
            </div>
            
            <button
              type="button"
              onClick={() => { setView('forgot'); setMessage(null); }}
              className="block w-full text-right text-sm text-purple-600 hover:text-purple-800 font-bold"
            >
              Forgot Password?
            </button>

            <button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-bold py-2 px-4 rounded-full hover:from-purple-600 hover:to-cyan-600 transition-colors disabled:opacity-50">
              {loading ? 'Logging In...' : 'Sign In'}
            </button>
          </form>
        )}

        {/* --- VIEW 2: FORGOT PASSWORD (Enter Email/Mobile) --- */}
        {view === 'forgot' && (
          <form onSubmit={handleSendOtp} className="space-y-4">
             <p className="text-center text-gray-500 text-sm mb-4">
               Enter your registered Email or Mobile. We will send an OTP to your registered Email.
             </p>
             <div className="relative">
               <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400"><Mail size={18} /></span>
               <input
                 type="text"
                 value={resetIdentifier}
                 onChange={(e) => setResetIdentifier(e.target.value)}
                 className="w-full pl-10 pr-3 py-2 border-b border-gray-300 focus:outline-none focus:border-purple-500"
                 placeholder="Enter Email or Mobile"
                 required
               />
             </div>
             <button type="submit" disabled={loading} className="w-full bg-purple-600 text-white font-bold py-2 px-4 rounded-full hover:bg-purple-700 disabled:opacity-50">
               {loading ? 'Sending OTP...' : 'Get OTP'}
             </button>
             <button type="button" onClick={() => setView('login')} className="w-full text-gray-500 text-sm flex items-center justify-center hover:text-gray-700">
               <ArrowLeft size={14} className="mr-1"/> Back to Login
             </button>
          </form>
        )}

        {/* --- VIEW 3: RESET PASSWORD (Enter OTP + New Pass) --- */}
        {view === 'reset' && (
          <form onSubmit={handleResetPassword} className="space-y-4">
             <p className="text-center text-gray-600 text-sm mb-4">
               OTP sent to: <strong>{maskedEmail}</strong>
             </p>
             <div className="relative">
               <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400"><KeyRound size={18} /></span>
               <input
                 type="text"
                 value={otp}
                 onChange={(e) => setOtp(e.target.value)}
                 className="w-full pl-10 pr-3 py-2 border-b border-gray-300 focus:outline-none focus:border-purple-500"
                 placeholder="Enter OTP"
                 required
               />
             </div>
             <div className="relative">
               <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400"><Lock size={18} /></span>
               <input
                 type="password"
                 value={newPassword}
                 onChange={(e) => setNewPassword(e.target.value)}
                 className="w-full pl-10 pr-3 py-2 border-b border-gray-300 focus:outline-none focus:border-purple-500"
                 placeholder="New Password"
                 required
               />
             </div>
             <button type="submit" disabled={loading} className="w-full bg-green-600 text-white font-bold py-2 px-4 rounded-full hover:bg-green-700 disabled:opacity-50">
               {loading ? 'Resetting...' : 'Set New Password'}
             </button>
             <button type="button" onClick={() => setView('login')} className="w-full text-gray-500 text-sm flex items-center justify-center hover:text-gray-700">
               Cancel
             </button>
          </form>
        )}

        {/* SIGNUP LINK (Only show on Login view) */}
        {view === 'login' && onShowSignup && (
          <p className="mt-6 text-center text-gray-600 text-sm">
            Don't have an account?{' '}
            <a href="#" className="text-purple-600 hover:text-purple-800 font-bold" onClick={(e) => { e.preventDefault(); onShowSignup(); }}>
              Sign Up
            </a>
          </p>
        )}
      </div>
    </div>
  );
};

export default LoginPage;